export const DIRECT_MESSAGING_MAX_XML_BYTES = 5 * 1024 * 1024;
export const DIRECT_MESSAGING_MAX_SUBJECT_LENGTH = 255;
export const DIRECT_MESSAGING_MAX_BODY_LENGTH = 10_000;
export const DIRECT_MESSAGING_XDM_SUBJECT_MARKER = 'XDM/1.0/DDM';

// The one dev console can send from either EMR Direct account: the production clinical
// address, or the phiMail sandbox test account (the same account the staging server uses).
export const DIRECT_MESSAGING_ACCOUNTS = ['production', 'test'] as const;
export type DirectMessagingAccount = (typeof DIRECT_MESSAGING_ACCOUNTS)[number];

export const DIRECT_MESSAGING_SENDERS: Record<DirectMessagingAccount, string> = {
  production: 'clinical@direct.nourish.com',
  test: 'usenourish@test.directproject.net',
};

export type DirectMessagingValidationSeverity = 'error' | 'warning';

export interface DirectMessagingValidationIssue {
  code: string;
  message: string;
  path: string | null;
  line: number | null;
  severity: DirectMessagingValidationSeverity;
  unconditional: boolean;
}

export interface DirectMessagingValidationResult {
  errors: DirectMessagingValidationIssue[];
  warnings: DirectMessagingValidationIssue[];
  isValid: boolean;
  canOverride: boolean;
  documentType: 'progress-note' | 'referral-note' | 'other-ccda' | 'xml' | 'unknown';
  attachmentMode: 'CDA' | 'RAW';
  informationRecipient: string | null;
}

export interface DirectMessagingDraft {
  recipient: string;
  subject: string;
  body: string;
  filename: string;
  xml: string;
  requestDeliveryNotification: boolean;
  /**
   * Wrap the CCDA in an IHE XDM ZIP with METADATA.XML, matching the production chart-note
   * send path. Defaults to true; only applies to clinical documents (attachmentMode CDA).
   * Set false to send the raw XML without packaging, for debugging.
   */
  packageAsXdm?: boolean;
}

export interface DirectMessagingCertificateMetadata {
  subject: string;
  issuer: string;
  validFrom: string;
  validTo: string;
  fingerprint256: string;
}

export interface DirectMessagingConfigResponse {
  account: DirectMessagingAccount;
  enabled: boolean;
  ready: boolean;
  sender: string;
  host: string | null;
  port: number | null;
  certificate: DirectMessagingCertificateMetadata | null;
  limits: {
    maxXmlBytes: number;
    maxSubjectLength: number;
    maxBodyLength: number;
  };
  missingVariables: string[];
}

export type DirectMessagingConfigsResponse = Record<DirectMessagingAccount, DirectMessagingConfigResponse>;

export interface DirectMessagingPollResponse {
  outcome: 'queued';
  account: DirectMessagingAccount;
  messageId: string;
  traceId: string;
  queuedAt: string;
}

export interface DirectMessagingFormalValidationIssue {
  assertionId: string | null;
  description: string;
  line: number | null;
  path: string | null;
  patternId: string | null;
  ruleId: string | null;
  test: string | null;
}

export interface DirectMessagingIgnoredValidationIssue extends DirectMessagingFormalValidationIssue {
  reason: string;
  severity: DirectMessagingValidationSeverity;
}

export interface DirectMessagingFormalValidationResponse {
  profile: string;
  profileVersion: string;
  advisory: true;
  durationMs: number;
  errors: DirectMessagingFormalValidationIssue[];
  warnings: DirectMessagingFormalValidationIssue[];
  ignored: DirectMessagingIgnoredValidationIssue[];
}

export interface DirectMessagingSendRequest {
  draft: DirectMessagingDraft;
  account: DirectMessagingAccount;
  allowInvalid: boolean;
  clientRequestId: string;
}

export type DirectMessagingSendOutcome = 'queued' | 'rejected' | 'failed';
export type DirectMessagingSendFailureCategory =
  | 'validation'
  | 'configuration'
  | 'certificate'
  | 'authentication'
  | 'recipient'
  | 'timeout'
  | 'protocol';

export interface DirectMessagingXdmInfo {
  /** Attachment filename actually sent (draft filename with .zip extension) */
  filename: string;
  zipByteCount: number;
  /** The generated METADATA.XML, for inspection */
  metadataXml: string;
}

export interface DirectMessagingSendResponse {
  outcome: DirectMessagingSendOutcome;
  summary: string;
  externalMessageId: string | null;
  failureCategory: DirectMessagingSendFailureCategory | null;
  durationMs: number;
  byteCount: number;
  sha256: string;
  validation: DirectMessagingValidationResult;
  cached: boolean;
  /** Present when the draft was packaged as an XDM ZIP */
  xdm?: DirectMessagingXdmInfo | null;
}

export interface DirectMessagingCcdaHtmlRequest {
  xml: string;
}

export interface DirectMessagingCcdaHtmlResponse {
  error: string | null;
  html: string | null;
}

export interface DirectMessagingXdmPreviewRequest {
  xml: string;
}

export interface DirectMessagingXdmPreviewResponse {
  error: string | null;
  metadataXml: string | null;
}

export interface DirectMessagingParseResult {
  error: string | null;
  structure: DirectMessagingParsedStructure | null;
}

export interface DirectMessagingParsedStructure {
  rootLocalName: string;
  rootNamespace: string | null;
  hl7RootChildNames: string[];
  templateIds: string[];
  hasNarrativeSection: boolean;
  informationRecipient: string | null;
}

export type DirectMessagingXmlParser = (xml: string) => DirectMessagingParseResult;

const CLINICAL_DOCUMENT_TEMPLATE_ID = '2.16.840.1.113883.10.20.22.1.1';
const PROGRESS_NOTE_TEMPLATE_ID = '2.16.840.1.113883.10.20.22.1.9';
const REFERRAL_NOTE_TEMPLATE_ID = '2.16.840.1.113883.10.20.22.1.14';

function getLine(xml: string, index: number): number {
  return xml.slice(0, Math.max(index, 0)).split('\n').length;
}

function issue(
  severity: DirectMessagingValidationSeverity,
  code: string,
  message: string,
  path: string | null,
  line: number | null,
  unconditional = false
): DirectMessagingValidationIssue {
  return { code, message, path, line, severity, unconditional };
}

export function isDirectAddress(value: string): boolean {
  return (
    value.length <= 320 &&
    /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+)+$/i.test(value) &&
    !/[\r\n]/.test(value)
  );
}

export function isDirectMessagingXmlFilename(value: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9._-]{0,250}\.xml$/i.test(value);
}

export function validateDirectMessagingXml(
  xml: string,
  envelopeRecipient: string,
  parseXml: DirectMessagingXmlParser
): DirectMessagingValidationResult {
  const errors: DirectMessagingValidationIssue[] = [];
  const warnings: DirectMessagingValidationIssue[] = [];
  const byteCount = new TextEncoder().encode(xml).byteLength;

  if (byteCount > DIRECT_MESSAGING_MAX_XML_BYTES) {
    errors.push(
      issue(
        'error',
        'payload_too_large',
        `XML is ${byteCount.toLocaleString()} bytes; the limit is ${DIRECT_MESSAGING_MAX_XML_BYTES.toLocaleString()} bytes.`,
        null,
        null,
        true
      )
    );
  }

  const declarationMatch = /<!\s*(DOCTYPE|ENTITY)\b/i.exec(xml);
  if (declarationMatch) {
    errors.push(
      issue(
        'error',
        'forbidden_xml_declaration',
        'DTD and entity declarations are not allowed.',
        null,
        getLine(xml, declarationMatch.index),
        true
      )
    );
  }

  const xmlDeclarationMatch = /^\uFEFF?\s*<\?xml\b[\s\S]*?\?>/i.exec(xml);
  const encodingMatch = /\bencoding\s*=\s*(['"])([^'"]+)\1/i.exec(xmlDeclarationMatch?.[0] ?? '');
  const declaredEncoding = encodingMatch?.[2];
  if (declaredEncoding && declaredEncoding.toLowerCase() !== 'utf-8') {
    errors.push(
      issue(
        'error',
        'unsupported_xml_encoding',
        'XML encoding declarations must be UTF-8 because Direct Messaging sends UTF-8 bytes.',
        null,
        xmlDeclarationMatch ? getLine(xml, xmlDeclarationMatch.index) : null,
        true
      )
    );
  }

  if (!xml.trim()) {
    errors.push(issue('error', 'empty_xml', 'XML is required.', null, null, true));
    return {
      errors,
      warnings,
      isValid: false,
      canOverride: false,
      documentType: 'unknown',
      attachmentMode: 'RAW',
      informationRecipient: null,
    };
  }

  if (errors.some((validationIssue) => validationIssue.unconditional)) {
    return {
      errors,
      warnings,
      isValid: false,
      canOverride: false,
      documentType: 'unknown',
      attachmentMode: 'RAW',
      informationRecipient: null,
    };
  }

  const parsed = parseXml(xml);
  if (parsed.error || !parsed.structure) {
    errors.push(
      issue(
        'error',
        'malformed_xml',
        `XML is not well formed: ${parsed.error ?? 'No document element was found.'}`,
        null,
        null
      )
    );
    return {
      errors,
      warnings,
      isValid: false,
      canOverride: !errors.some((validationIssue) => validationIssue.unconditional),
      documentType: 'unknown',
      attachmentMode: 'RAW',
      informationRecipient: null,
    };
  }

  const { structure } = parsed;
  const isClinicalDocument = structure.rootLocalName === 'ClinicalDocument';
  if (!isClinicalDocument) {
    errors.push(
      issue(
        'error',
        'not_clinical_document',
        'The root element is not ClinicalDocument. Sending it anyway will use ADD RAW.',
        '/',
        1
      )
    );
  }

  if (isClinicalDocument) {
    if (structure.rootNamespace !== 'urn:hl7-org:v3') {
      errors.push(
        issue(
          'error',
          'missing_hl7_namespace',
          'ClinicalDocument must use the urn:hl7-org:v3 namespace.',
          '/ClinicalDocument',
          1
        )
      );
    }
  }

  const ids = structure.templateIds;
  if (isClinicalDocument && !ids.includes(CLINICAL_DOCUMENT_TEMPLATE_ID)) {
    errors.push(
      issue(
        'error',
        'missing_us_realm_template',
        `Missing US Realm Header templateId ${CLINICAL_DOCUMENT_TEMPLATE_ID}.`,
        '/ClinicalDocument/templateId',
        1
      )
    );
  }

  let documentType: DirectMessagingValidationResult['documentType'] = isClinicalDocument ? 'other-ccda' : 'xml';
  if (ids.includes(PROGRESS_NOTE_TEMPLATE_ID)) documentType = 'progress-note';
  if (ids.includes(REFERRAL_NOTE_TEMPLATE_ID)) documentType = 'referral-note';

  if (isClinicalDocument && documentType === 'other-ccda') {
    warnings.push(
      issue(
        'warning',
        'unrecognized_document_template',
        'No recognized Progress Note or Referral Note document template was found.',
        '/ClinicalDocument/templateId',
        1
      )
    );
  }

  if (isClinicalDocument) {
    for (const [element, label] of [
      ['id', 'document id'],
      ['code', 'document code'],
      ['title', 'title'],
      ['effectiveTime', 'effective time'],
      ['confidentialityCode', 'confidentiality code'],
      ['languageCode', 'language code'],
      ['recordTarget', 'record target'],
      ['author', 'author'],
      ['custodian', 'custodian'],
    ] as const) {
      if (!structure.hl7RootChildNames.includes(element)) {
        errors.push(
          issue(
            'error',
            `missing_${element}`,
            `Missing required C-CDA header ${label}.`,
            `/ClinicalDocument/${element}`,
            1
          )
        );
      }
    }

    if (!structure.hasNarrativeSection) {
      errors.push(
        issue(
          'error',
          'missing_narrative_section',
          'A C-CDA document must contain at least one section with narrative text.',
          '/ClinicalDocument/component/structuredBody',
          null
        )
      );
    }
  }

  const informationRecipient = structure.informationRecipient;
  if (isClinicalDocument && !informationRecipient) {
    warnings.push(
      issue(
        'warning',
        'missing_information_recipient',
        'The C-CDA header does not contain an informationRecipient Direct address.',
        '/ClinicalDocument/informationRecipient',
        null
      )
    );
  } else if (
    informationRecipient &&
    envelopeRecipient.trim() &&
    informationRecipient.toLowerCase() !== envelopeRecipient.trim().toLowerCase()
  ) {
    warnings.push(
      issue(
        'warning',
        'recipient_mismatch',
        `Envelope recipient ${envelopeRecipient.trim()} does not match informationRecipient ${informationRecipient}.`,
        '/ClinicalDocument/informationRecipient/intendedRecipient/telecom',
        null
      )
    );
  }

  return {
    errors,
    warnings,
    isValid: errors.length === 0,
    canOverride: !errors.some((validationIssue) => validationIssue.unconditional),
    documentType,
    attachmentMode: isClinicalDocument ? 'CDA' : 'RAW',
    informationRecipient,
  };
}
